// Hydra series fee schedule client — fetch once at session start.
//
// Spec: HYDRA_API.md → "Series Fee Schedule".
// `/api/analytics/series/fees` lives on hydra's analytics surface (the only
// hydra REST surface the g1↔hydra relay externalizes with CORS), so it
// shares the same VITE_HYDRA_ANALYTICS_URL + bearer the rest of g1's
// analytics consumption uses (see services/hydraAnalytics.ts).
//
// Hydra refreshes upstream hourly, so a per-session fetch is sufficient.
// Do not poll.

import { create } from 'zustand';

const BASE = (import.meta.env.VITE_HYDRA_ANALYTICS_URL as string | undefined)?.trim() || '';
const TOKEN = (import.meta.env.VITE_HYDRA_ANALYTICS_TOKEN as string | undefined)?.trim() || '';

export type FeeType = 'quadratic' | 'quadratic_with_maker_fees' | 'flat';

export interface SeriesFeeEntry {
  feeType: FeeType;
  feeMultiplier: number;
  /** Effective taker coefficient (already × multiplier). Plug into × P × (1−P). Undefined for `flat`. */
  feeTakerRate?: number;
  /** Effective maker coefficient. 0 for plain quadratic, ~0.0175 × multiplier for quadratic_with_maker_fees, undefined for flat. */
  feeMakerRate?: number;
}

export interface SeriesFeesResponse {
  fees: Record<string, SeriesFeeEntry>;
  generatedAt: string;
}

const KALSHI_SERIES_PREFIX = /^(KX[A-Z]+?)(?=-)/;

interface FeeState {
  ready: boolean;
  error: string | null;
  fees: Record<string, SeriesFeeEntry>;
  generatedAt: string | null;
  loadFees: () => Promise<void>;
}

let inFlight: Promise<void> | null = null;

export const useFeeStore = create<FeeState>((set, get) => ({
  ready: false,
  error: null,
  fees: {},
  generatedAt: null,
  loadFees: () => {
    if (get().ready) return Promise.resolve();
    if (inFlight) return inFlight;
    inFlight = doFetch()
      .then((body) => {
        set({ ready: true, fees: body.fees ?? {}, generatedAt: body.generatedAt, error: null });
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err);
        set({ error: msg });
        // Keep ready=false; consumers render loading state. A retry can be triggered explicitly.
        throw err;
      })
      .finally(() => {
        inFlight = null;
      });
    return inFlight;
  },
}));

async function doFetch(): Promise<SeriesFeesResponse> {
  if (!BASE) {
    throw new Error('VITE_HYDRA_ANALYTICS_URL not set — cannot fetch fee schedule');
  }
  const url = `${BASE.replace(/\/$/, '')}/api/analytics/series/fees`;
  const headers: HeadersInit = TOKEN ? { Authorization: `Bearer ${TOKEN}` } : {};

  // Brief retry on 503 FEES_NOT_READY (cold start — typically within 60s of
  // hydra boot, fee registry intentionally trails discovery).
  const maxAttempts = 8;
  const delayMs = 400;
  let lastErr: unknown;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const res = await fetch(url, { headers });
      if (res.status === 503) {
        let code: string | undefined;
        try {
          const body = (await res.json()) as { code?: string };
          code = body?.code;
        } catch {
          // ignore parse failure
        }
        if (code === 'FEES_NOT_READY') {
          await sleep(delayMs);
          continue;
        }
        throw new Error(`hydra fees: 503 ${code ?? ''}`);
      }
      if (!res.ok) {
        throw new Error(`hydra fees: ${res.status} ${res.statusText}`);
      }
      return (await res.json()) as SeriesFeesResponse;
    } catch (err) {
      lastErr = err;
      if (attempt === maxAttempts - 1) break;
      await sleep(delayMs);
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error('hydra fees: exhausted retries');
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

/**
 * Sync lookup. Returns undefined for non-Kalshi tickers, unknown series,
 * or before the cache is populated. Use {@link useFeeFor} in React components
 * to re-render when the cache loads.
 */
export function feeFor(ticker: string | null | undefined): SeriesFeeEntry | undefined {
  if (!ticker) return undefined;
  const m = KALSHI_SERIES_PREFIX.exec(ticker);
  if (!m) return undefined;
  return useFeeStore.getState().fees[m[1]];
}

/** React hook that returns the fee entry for a ticker and re-renders when it loads. */
export function useFeeFor(ticker: string | null | undefined): SeriesFeeEntry | undefined {
  const fees = useFeeStore((s) => s.fees);
  if (!ticker) return undefined;
  const m = KALSHI_SERIES_PREFIX.exec(ticker);
  if (!m) return undefined;
  return fees[m[1]];
}

/** React hook for components that just need to know "are fees loaded?". */
export function useFeesReady(): boolean {
  return useFeeStore((s) => s.ready);
}

// ─── Test seam ───────────────────────────────────────────────────────
// Tests can pre-populate the store rather than hit the network.
export function __setFeesForTests(fees: Record<string, SeriesFeeEntry>): void {
  useFeeStore.setState({ ready: true, fees, generatedAt: new Date().toISOString(), error: null });
}

export function __resetFeesForTests(): void {
  inFlight = null;
  useFeeStore.setState({ ready: false, fees: {}, generatedAt: null, error: null });
}
